import ApiService from '../api.service';
import { FirebaseFunctionNames } from 'core/constants/firebase-function-names';

/**
 * The Delivery Request reporting numbers, and the same range as a spreadsheet.
 *
 * Counted in the backend rather than in the browser. The screen shows a date
 * range rather than a page, so counting what happened to be loaded would
 * report a fraction of the answer, and the export has to agree with the charts
 * it was taken from — one query behind both is the only way that holds.
 *
 * Mirrors functions/src/services/delivery-request-reporting.ts.
 */

export type ReportGranularity = 'day' | 'week';

/** Which of these a request lands in is decided by its status alone. */
export interface IOutcomeCounts {
  total: number;
  /** Booked into the delivery service. */
  inHouse: number;
  /** Emailed out to another team or supplier. */
  forwarded: number;
  /** Closed by a person without going anywhere. */
  closed: number;
  /** Still waiting on somebody. */
  awaiting: number;
  /** Arrived but could not be read. */
  unreadable: number;
}

export interface IBreakdownRow extends IOutcomeCounts {
  label: string;
}

export interface IForwardTargetRow {
  label: string;
  email: string;
  total: number;
  /** Share of everything forwarded that went here, 0-1. */
  share: number;
}

export interface IPeriodRow extends IOutcomeCounts {
  period: string;
  label: string;
}

/**
 * The share of fulfilled work somebody else did.
 *
 * `resolved` is in-house plus forwarded, and deliberately not everything that
 * arrived: a request still waiting, closed as a duplicate, or unreadable was
 * not resolved by a third party, and counting it would understate the number.
 */
export interface IMissedOpportunity {
  resolved: number;
  inHouse: number;
  forwarded: number;
  forwardedShare: number;
}

export interface IDeliveryRequestReport {
  range: { startDate: string; endDate: string; granularity: ReportGranularity };
  totals: IOutcomeCounts;
  missedOpportunity: IMissedOpportunity;
  byRequestType: IBreakdownRow[];
  byForwardTarget: IForwardTargetRow[];
  byCollectionSite: IBreakdownRow[];
  byDestinationSite: IBreakdownRow[];
  byDepartment: IBreakdownRow[];
  overTime: IPeriodRow[];
}

/** A row per request, or the aggregate behind the charts. */
export type ExportShape = 'requests' | 'summary';

const DeliveryRequestReportsApi = {
  get: (startDate: string, endDate: string): Promise<IDeliveryRequestReport> =>
    ApiService.callFunction<IDeliveryRequestReport>(
      { startDate, endDate },
      FirebaseFunctionNames.GET_DELIVERY_REQUEST_REPORT,
    ),

  /** Returns CSV text for the caller to save. */
  exportCsv: (startDate: string, endDate: string, shape: ExportShape): Promise<string> =>
    ApiService.callFunction<string>(
      { startDate, endDate, shape },
      FirebaseFunctionNames.EXPORT_DELIVERY_REQUEST_REPORT,
    ),
};

export default DeliveryRequestReportsApi;
